import { isSameDay, format } from 'date-fns';

export interface DailyCollection {
  sale_id: number;
  notes?: string;
  customer_name: string;
  customer_surname: string;
  customer_address: string;
  customer_phone?: string;
  product_name: string;
  installment_number: number;
  total_installments: number;
  installment_amount: number;
  cash_amount?: number;
  transfer_amount?: number;
  payment_method?: string;
  is_paid: boolean;
  is_rescheduled?: boolean;
  due_date: string;
  week_number: number;
  collection_day: number;
  payment_id?: number;
  payment_date?: string;
  total_paid_amount?: number;
  remaining_balance?: number;
  payments_count?: number;
  is_partial?: boolean;
  last_payment_date?: string;
  last_payment_note?: string;
  last_reschedule_reason?: string;
  rescheduled_to_date?: string;
  vendor_name?: string;
  collector_name?: string;
  overdue_count?: number;
  overdue_details?: { installment_number: number; amount: number; owed: number; due_date: string }[];
  is_returned?: boolean;
  last_sale_payment_id?: number;
  last_sale_payment_date?: string;
}

export interface CollectionStatus {
  label: string;
  color: string;
  bgColor: string;
}

export const getStartOfWeek = (date: Date): Date => {
  const wed = new Date(date);
  wed.setHours(0, 0, 0, 0);
  const jsDay = wed.getDay();
  const offsetToWednesday = (jsDay + 4) % 7;
  wed.setDate(wed.getDate() - offsetToWednesday);
  return wed;
};

export const parseDateValue = (value: string): Date => {
  const [datePart] = value.split('T');
  const parts = datePart.split('-').map(Number);
  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return new Date(value);
};

export const formatDueDateDisplay = (value: string): string => {
  const date = parseDateValue(value);
  return Number.isNaN(date.getTime()) ? value : format(date, 'dd-MM-yyyy');
};

export const roundCurrency = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

export const computeRemainingBalance = (collection: DailyCollection): number => {
  const totalPaid = collection.total_paid_amount ?? 0;
  const backendRemaining = collection.remaining_balance;
  const rawRemaining = typeof backendRemaining === 'number'
    ? backendRemaining
    : collection.installment_amount - totalPaid;
  return roundCurrency(Math.max(rawRemaining, 0));
};

export const getCollectionStatus = (collection: DailyCollection): CollectionStatus => {
  if (collection.is_returned) {
    return { label: 'Devuelto', color: '#8B5CF6', bgColor: 'rgba(139,92,246,0.15)' };
  }
  if (collection.is_paid) {
    return { label: 'Cobrado', color: '#10B981', bgColor: 'rgba(16,185,129,0.15)' };
  }
  if (collection.is_partial) {
    return { label: 'Pago parcial', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.15)' };
  }
  if (collection.is_rescheduled) {
    const toDate = collection.rescheduled_to_date ? formatDueDateDisplay(collection.rescheduled_to_date) : '';
    const label = toDate ? `Reprog. al ${toDate}` : 'Reprogramado';
    return { label, color: '#9CA3AF', bgColor: 'rgba(156,163,175,0.15)' };
  }
  const dueDate = parseDateValue(collection.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  if (dueDate < today) {
    return { label: 'Vencido', color: '#EF4444', bgColor: 'rgba(239,68,68,0.15)' };
  }
  if (isSameDay(dueDate, today)) {
    return { label: 'Vence hoy', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.15)' };
  }
  return { label: 'Pendiente', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.1)' };
};

export const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
